import { Divider, Drawer, Radio, Select, Slider, Switch, Tag } from "antd";
import { useEffect } from "react";
import styled from "styled-components";

import {
  FilterConfig,
  FilterMode,
  profileToConfig,
} from "../../../utils/telephonyFilters";
import {
  DEFAULT_STT_MODEL,
  STT_MODEL_SELECT_OPTIONS,
} from "../sttModelOptions";

/**
 * Settings drawer that exposes the same telephony-simulation controls as
 * `/phone-sim`: profile preset, per-stage toggles (bandpass, µ-law,
 * noise + SNR), per-side toggles, and the mic uplink sample rate.
 *
 * Filter graph is captured at connect time on the underlying audio
 * managers — only `noise` toggle and SNR slider are reactive (they
 * adjust a GainNode). Everything else takes effect on the next call.
 */

interface PhoneSimSettingsProps {
  open: boolean;
  onClose: () => void;
  filterMode: FilterMode;
  setFilterMode: (m: FilterMode) => void;
  filterConfig: FilterConfig;
  setFilterConfig: (c: FilterConfig) => void;
  inputSampleRate: number;
  setInputSampleRate: (sr: number) => void;
  /** Per-call Khmer STT model: "v1" (model 1) or "v5" (model 2). */
  sttModel: string;
  setSttModel: (m: string) => void;
  /** When the call is connected, locks the chain-changing controls so the user
   * isn't surprised that, e.g., toggling "bandpass" mid-call has no effect. */
  callConnected: boolean;
}

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0;
`;

const Label = styled.span`
  color: #4b5563;
  font-size: 13px;
`;

const Hint = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
`;

export const PhoneSimSettings = ({
  open,
  onClose,
  filterMode,
  setFilterMode,
  filterConfig,
  setFilterConfig,
  inputSampleRate,
  setInputSampleRate,
  sttModel,
  setSttModel,
  callConnected,
}: PhoneSimSettingsProps) => {
  // Selecting a profile preset overwrites the underlying config; flipping
  // an individual switch transitions to "custom" so the user knows their
  // picks no longer match a named preset.
  useEffect(() => {
    setFilterConfig(profileToConfig(filterMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode]);

  const update = <K extends keyof FilterConfig>(key: K, value: FilterConfig[K]) => {
    setFilterConfig({ ...filterConfig, [key]: value });
    if (filterMode !== "custom") setFilterMode("custom");
  };

  const summary = (() => {
    const parts: string[] = [];
    if (filterConfig.bandpass) parts.push("Bandpass 300–3400 Hz");
    if (filterConfig.ulaw) parts.push("G.711 µ-law");
    if (filterConfig.noise) parts.push(`Noise (SNR ${filterConfig.noiseSnrDb} dB)`);
    if (parts.length === 0) return "No phone-like processing — clean PCM";
    const sides = [
      filterConfig.applyOnInput ? "input" : null,
      filterConfig.applyOnOutput ? "output" : null,
    ]
      .filter(Boolean)
      .join(" + ");
    return `${parts.join(", ")} • ${sides || "no side"}`;
  })();

  return (
    <Drawer
      title="Phone simulation settings"
      open={open}
      onClose={onClose}
      width={420}
      destroyOnClose={false}
    >
      <Hint style={{ marginBottom: 12 }}>
        Apply a telephony-style processing chain to the call so testers can
        hear / be heard the way they would on a real mobile call (bandpass
        300–3400 Hz, AGC, optional G.711 µ-law roundtrip, optional line
        noise). The graph is rebuilt on each Call — chain options take
        effect on the next reconnect; only noise toggle and SNR are live.
      </Hint>

      <Row>
        <Label>Khmer STT model</Label>
        <Select
          value={sttModel || DEFAULT_STT_MODEL}
          onChange={setSttModel}
          disabled={callConnected}
          style={{ width: 260 }}
          options={STT_MODEL_SELECT_OPTIONS}
        />
      </Row>
      <Hint style={{ marginBottom: 8 }}>
        Chooses which Khmer speech-to-text model the backend runs for this
        call (model 1, or the parakeet-v5 / parakeet-v4 checkpoints). Takes
        effect on the next Call.
      </Hint>

      <Divider style={{ margin: "10px 0" }} />

      <Row>
        <Label>Mic uplink rate</Label>
        <Select
          value={inputSampleRate}
          onChange={setInputSampleRate}
          disabled={callConnected}
          style={{ width: 220 }}
          options={[
            { value: 16000, label: "16 kHz (default — wideband)" },
            { value: 8000, label: "8 kHz (telephony narrowband)" },
          ]}
        />
      </Row>

      <Divider style={{ margin: "10px 0" }} />

      <Row>
        <Label>Profile</Label>
        <Radio.Group
          value={filterMode}
          disabled={callConnected}
          onChange={(e) => setFilterMode(e.target.value as FilterMode)}
          optionType="button"
          buttonStyle="solid"
          size="small"
          options={[
            { label: "Off", value: "off" },
            { label: "Light", value: "light" },
            { label: "Full", value: "full" },
            { label: "Custom", value: "custom" },
          ]}
        />
      </Row>

      <Row>
        <Label>Bandpass 300–3400 Hz + AGC</Label>
        <Switch
          checked={filterConfig.bandpass}
          disabled={callConnected}
          onChange={(v) => update("bandpass", v)}
        />
      </Row>
      <Row>
        <Label>G.711 µ-law roundtrip</Label>
        <Switch
          checked={filterConfig.ulaw}
          disabled={callConnected}
          onChange={(v) => update("ulaw", v)}
        />
      </Row>

      <Row>
        <Label>Line noise (live)</Label>
        <Switch
          checked={filterConfig.noise}
          onChange={(v) => update("noise", v)}
        />
      </Row>
      {filterConfig.noise && (
        <Row>
          <Label>Noise SNR (dB) — higher = quieter</Label>
          <Slider
            min={10}
            max={45}
            value={filterConfig.noiseSnrDb}
            onChange={(v) => update("noiseSnrDb", v)}
            style={{ width: 200 }}
          />
        </Row>
      )}

      <Divider style={{ margin: "10px 0" }} />

      <Row>
        <Label>Apply on input (mic → server)</Label>
        <Switch
          checked={filterConfig.applyOnInput}
          disabled={callConnected}
          onChange={(v) => update("applyOnInput", v)}
        />
      </Row>
      <Row>
        <Label>Apply on output (server → speaker)</Label>
        <Switch
          checked={filterConfig.applyOnOutput}
          disabled={callConnected}
          onChange={(v) => update("applyOnOutput", v)}
        />
      </Row>

      <div style={{ marginTop: 16 }}>
        <Tag color="blue" style={{ whiteSpace: "normal" }}>
          {summary}
        </Tag>
      </div>

      {callConnected && (
        <Hint style={{ marginTop: 14, color: "#a16207" }}>
          A call is in progress — chain-changing options are locked. Hang up
          and reconnect to apply them. Noise toggle and SNR slider stay live.
        </Hint>
      )}
    </Drawer>
  );
};
